import { Component, OnDestroy, OnInit } from '@angular/core';
import { ComplexWallItem } from '../../common/types/wall-item';
import { TilesService } from '../../services/tiles.service';
import { BaseComponent } from '../../common/base/base.component';
import { catchError, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { EthereumService } from '../../services/ethereum.service';
import { CommonService } from '../../services/common.service';
import { PopupsManagerService } from '../../services/popups-manager.service';
import {
  PurchaseInfoPopupComponent,
  PurchaseInfoType,
} from '../purchase-info-popup/purchase-info-popup.component';
import { Observable, of, throwError, timer } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent extends BaseComponent implements OnInit {
  public cartItems: ComplexWallItem[] = [];
  public totalEtheriumPrice: number = 0;
  public totalUSDPrice: number = 0;
  public loading: boolean = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private cartService: TilesService,
    private commonService: CommonService,
    public ethereumService: EthereumService,
    private popupsManagerService: PopupsManagerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.fetchCartItems();
  }

  public removeItem(id: string): void {
    this.commonService.loadingChange(true, true);
    this.cartService
      .removeCartItem(id)
      .pipe(
        takeUntil(this.destroy$),
        catchError((e) => {
          if (e.status === 409) {
            const componentRef = this.popupsManagerService.show(
              PurchaseInfoPopupComponent,
              {
                inputs: {
                  type: PurchaseInfoType.Error,
                  info: {
                    title: 'Unavailable Item!',
                    text: 'This item has no longer available',
                  },
                },
                className: 'overlay',
              }
            );
            componentRef.instance.afterPopupClosed.subscribe(() => {
              this.cartService
                .fetchCartItems()
                .pipe(takeUntil(this.destroy$))
                .subscribe();
              this.cartService
                .fetchWallTiles()
                .pipe(takeUntil(this.destroy$))
                .subscribe();
            });
          }
          return of(null);
        }),
        finalize(() => this.commonService.loadingChange(false))
      )
      .subscribe();
  }

  public handleCheckout(): void {
    this.commonService.loadingChange(true, true);
    const tiles = this.cartItems.reduce(
      (tiles: number[], cartItem: ComplexWallItem) => [
        ...cartItem.tiles.map((t) => Number(t.id)),
        ...tiles,
      ],
      []
    );
    this.authService.checkNetworkAndSwitch().then(() => {
      this.cartService
        .preCheckout()
        .pipe(
          switchMap(() => this.cartService.mint(tiles)),
          catchError((e) => this.openPurchaseErrorPopup(e)),
          finalize(() => this.commonService.loadingChange(false))
        )
        .subscribe((done: boolean) => {
          if (done) {
            this.openPurchaseSuccessPopup();
            this.cartService.fetchWallTiles().subscribe();
            this.cartService.emptyCart();
          }
        });
    });
  }

  private openPurchaseSuccessPopup(): void {
    timer(3000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.router.navigateByUrl('/');
        this.popupsManagerService.hide();
      });
    this.popupsManagerService.show(PurchaseInfoPopupComponent, {
      inputs: {
        type: PurchaseInfoType.Success,
        info: {
          title: 'Thank you!',
          text: ' You successfully posted your art!',
          subText: "Now it's wide open for everyone to see.",
        },
      },
      className: 'overlay',
    });
  }

  private getTotalPrices(): void {
    const tiles = this.cartItems.map((cartItem) => cartItem.tiles).flat();
    this.totalEtheriumPrice = tiles.reduce((eth: number, cartItem) => {
      eth += cartItem.price || 0;
      return eth;
    }, 0);
    this.totalUSDPrice =
      this.totalEtheriumPrice * this.ethereumService.ethereumPrice;
  }

  private fetchCartItems(): void {
    this.cartService.cartItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cartItems: ComplexWallItem[]) => {
        this.cartItems = cartItems;
        this.getTotalPrices();
      });
  }

  private openPurchaseErrorPopup(errorMessage: {
    message: string;
    stack: string;
    code: number;
  }): Observable<never> {
    this.popupsManagerService.show(PurchaseInfoPopupComponent, {
      inputs: {
        type: PurchaseInfoType.Error,
        info: {
          title: 'Transaction failed',
          text: 'Something went wrong with your transaction.',
          subText: 'Please try again.',
        },
      },
      className: 'overlay',
    });
    return throwError(errorMessage);
  }
}
